import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { Observable, catchError, of } from 'rxjs';
import { UAParser } from 'ua-parser-js';
import { ConfigService } from '../config-service/config-service.service';

interface DeviceEnrollmentRequest {
  publicKey: string;
  deviceId: string;
  deviceInfo: string;
  alias: string;
}

interface ReplaceEnrollmentRequest extends DeviceEnrollmentRequest {
  oldAlias: string;
}

interface EnrolledDevice {
  deviceId: string;
  alias: string;
  deviceInfo: string;
}

interface EnrolledDevices {
  credentials: EnrolledDevice[];
}

@Injectable({
  providedIn: 'root',
})
export class DeviceEnrollmentService {
  private baseUrl: string;
  private parser: UAParser;

  constructor(private http: HttpClient, private configService: ConfigService) {
    this.baseUrl = this.configService.config.api_endpoints['account_service'] || '';
    // add last slash if not present to ensure correct URL construction
    if (!this.baseUrl.endsWith('/')) {
      this.baseUrl += '/';
    }
    this.parser = new UAParser();
  }

  getDeviceInfo(): { deviceId: string; deviceModel: string } {
    // Generate a persistent device ID
    let deviceId = localStorage.getItem('deviceId');
    if (!deviceId) {
      deviceId = this.generateDeviceId();
      localStorage.setItem('deviceId', deviceId);
    }

    const result = this.parser.getResult();
    const deviceModel = `${result.os.name || 'Unknown OS'} ${result.os.version || ''} - ${
      result.browser.name || 'Unknown Browser'
    } ${result.browser.version || ''}`.trim();

    return {
      deviceId,
      deviceModel,
    };
  }

  private generateDeviceId(): string {
    const result = this.parser.getResult();

    // Create a fingerprint based on consistent browser/device characteristics
    const fingerprint = [
      result.browser.name || '',
      result.browser.version || '',
      result.os.name || '',
      result.os.version || '',
      result.engine.name || '',
      navigator.language,
      navigator.hardwareConcurrency || 0,
      new Date().getTimezoneOffset(),
      screen.width,
      screen.height,
      screen.colorDepth,
    ].join('|');

    // Generate a consistent hash using a simple but effective hashing algorithm
    let hash1 = 0;
    let hash2 = 0;

    for (let i = 0; i < fingerprint.length; i++) {
      const char = fingerprint.charCodeAt(i);
      hash1 = (hash1 << 5) - hash1 + char;
      hash1 = hash1 & hash1; // Convert to 32bit integer

      // Second hash for more entropy
      hash2 = char + (hash2 << 6) + (hash2 << 16) - hash2;
    }

    const hash1Str = Math.abs(hash1).toString(36);
    const hash2Str = Math.abs(hash2).toString(36);

    return `web-${hash1Str}-${hash2Str}`;
  }

  enrollDevice(token: string, data: DeviceEnrollmentRequest): Observable<boolean> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });

    return this.http.post<any>(`${this.baseUrl}device-enrolment/enroll`, data, { headers }).pipe(
      catchError((error) => {
        console.error('Device enrollment error:', error);
        Sentry.captureException(error);
        return of(false);
      }),
    );
  }

  replaceEnrolledDevice(token: string, data: ReplaceEnrollmentRequest): Observable<boolean> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });

    return this.http.post<any>(`${this.baseUrl}device-enrolment/replace-enroll`, data, { headers }).pipe(
      catchError((error) => {
        console.error('Replace device enrollment error:', error);
        Sentry.captureException(error);
        return of(false);
      }),
    );
  }

  getEnrolledDevices(token: string): Observable<EnrolledDevices | null> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });

    return this.http
      .get<EnrolledDevices>(`${this.baseUrl}device-enrolment/enrolled-devices`, { headers })
      .pipe(
        catchError((error) => {
          console.error('Get enrolled devices error:', error);
          Sentry.captureException(error);
          return of(null);
        }),
      );
  }
}
